import React from 'react'
import { cn } from 'lib/utils'
import { FIGMA_KANBAN_COLUMN, type FigmaColumnTheme } from './figmaKanbanTheme'

interface FigmaKanbanColumnShellProps {
  title: string
  count: number
  theme: FigmaColumnTheme
  children: React.ReactNode
  setDroppableRef: (node: HTMLElement | null) => void
  isOver?: boolean
  headerActions?: React.ReactNode
  footer?: React.ReactNode
}

/** Shared 232×372 Figma column layout for all hiring steps. */
export function FigmaKanbanColumnShell({
  title,
  count,
  theme,
  children,
  setDroppableRef,
  isOver = false,
  headerActions,
  footer,
}: FigmaKanbanColumnShellProps) {
  return (
    <div
      className="flex shrink-0 flex-col justify-between overflow-hidden rounded-[6px] border bg-white dark:border-border dark:bg-card"
      style={{
        width: FIGMA_KANBAN_COLUMN.width,
        height: FIGMA_KANBAN_COLUMN.height,
        borderColor: FIGMA_KANBAN_COLUMN.borderColor,
      }}
    >
      <div
        className="flex shrink-0 items-center justify-between rounded-t-[6px]"
        style={{
          height: FIGMA_KANBAN_COLUMN.headerHeight,
          padding: FIGMA_KANBAN_COLUMN.headerPadding,
          backgroundColor: theme.headerBg,
        }}
      >
        <div className="flex min-w-0 items-center gap-1">
          <h3
            className={cn('truncate', theme.titleClass)}
            style={theme.titleStyle ?? { fontWeight: 700 }}
            title={title}
          >
            {title}
          </h3>
          <span
            className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold leading-none text-white"
            style={{ backgroundColor: theme.accent }}
          >
            {count}
          </span>
        </div>
        {headerActions ? <div className="flex shrink-0 items-center gap-1">{headerActions}</div> : null}
      </div>

      <div
        ref={setDroppableRef}
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-y-auto border-x bg-white custom-scrollbar dark:bg-card',
          isOver && theme.isOverClass
        )}
        style={{ borderColor: FIGMA_KANBAN_COLUMN.borderColor }}
      >
        {children}
      </div>

      {footer}
    </div>
  )
}
